import React from 'react';
import PropTypes from 'prop-types';
import {Card, CardContent, CardActionArea, Typography, Grid} from '../components';

const CardSelector = (props) => {
  const {
    options = [],
    selectedValue = null,
    onSelectionChange,
    title,
    description,
    multiSelect = false,
    gridProps = {size: {xs: 6, sm: 3}},
    cardHeight = '100%',
    iconSize = 40,
    ...other
  } = props;

  // Handle single or multi-select
  const handleOptionToggle = (optionId) => {
    if (!onSelectionChange) return;

    if (multiSelect) {
      // Multi-select logic
      const currentSelections = Array.isArray(selectedValue) ? selectedValue : [];
      const isSelected = currentSelections.includes(optionId);

      const updatedSelections = isSelected ?
        currentSelections.filter((id) => id !== optionId) :
        [...currentSelections, optionId];

      onSelectionChange(updatedSelections);
    } else {
      // Single-select logic
      const newSelection = selectedValue === optionId ? null : optionId;
      onSelectionChange(newSelection);
    }
  };

  // Check if an option is selected
  const isOptionSelected = (optionId) => {
    if (multiSelect) {
      return Array.isArray(selectedValue) && selectedValue.includes(optionId);
    }
    return selectedValue === optionId;
  };

  return (
    <div {...other}>
      {title && (
        <Typography variant="h6" gutterBottom sx={{mt: 3, mb: 2}}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          {description}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options.map((option) => {
          const {id, label, icon: IconComponent, description: optionDescription} = option;
          const isSelected = isOptionSelected(id);

          return (
            <Grid {...gridProps} key={id}>
              <Card
                variant="outlined"
                sx={{
                  height: cardHeight,
                  transition: 'all 0.2s ease-in-out',
                  ...(isSelected && {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.light',
                    boxShadow: 2,
                    cursor: 'pointer',
                  }),
                }}
              >
                <CardActionArea
                  onClick={() => handleOptionToggle(id)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                  }}
                >
                  <CardContent
                    sx={{
                      'textAlign': 'center',
                      'display': 'flex',
                      'flexDirection': 'column',
                      'alignItems': 'center',
                      'gap': 1,
                      'p': 0,
                      '&:last-child': {'pb': 0},
                    }}
                  >
                    {IconComponent && (
                      <IconComponent
                        sx={{
                          fontSize: iconSize,
                          color: isSelected ? 'primary.main' : 'text.secondary',
                          transition: 'color 0.2s ease-in-out',
                        }}
                      />
                    )}
                    <Typography
                      variant="subtitle1"
                      fontWeight={isSelected ? 600 : 500}
                      color={isSelected ? 'primary.main' : 'text.primary'}
                    >
                      {label}
                    </Typography>
                    {optionDescription && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{fontSize: '0.75rem'}}
                      >
                        {optionDescription}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

CardSelector.propTypes = {
  // Array of options to display
  options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
        icon: PropTypes.elementType, // React component for icon
        description: PropTypes.string, // Optional description text
      }),
  ).isRequired,

  // Current selection(s)
  selectedValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),

  // Callback when selection changes
  onSelectionChange: PropTypes.func,

  // Optional title above the selector
  title: PropTypes.string,

  // Optional description below title
  description: PropTypes.string,

  // Enable multi-select mode
  multiSelect: PropTypes.bool,

  // Grid item props for each card
  gridProps: PropTypes.object,

  // Height of each card
  cardHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // Size of icons in cards
  iconSize: PropTypes.number,
};

export default React.memo(CardSelector);
